/**
 * Zwift Click (and Play/Ride) controller over BLE (SPEC §5.1, PROTOCOLS.md §6). Experimental, behind a flag.
 * Emits raw button state changes; debounce/repeat/mapping happen in the input layer.
 */
import type { BleDevice, GattCharacteristic, GattServer, GattService } from '@/ble/types';
import { subscribe, tryGetService, writeChar } from '@/ble/gatt';
import { asBytes, toHex } from '@/ble/bytes';
import { UUID } from '@/ble/uuids';
import { Emitter } from '@/trainer/types';
import { readBattery } from '../battery';
import type { ButtonEvent, ConnectableController } from './types';
import {
  decryptPacket,
  deriveSessionKeys,
  generateKeyPair,
  sharedSecret,
  type EcdhKeyPair,
  type SessionKeys,
} from './crypto';
import {
  buildEncryptedHandshake,
  buildPlainHandshake,
  isLockedResponse,
  isRideOn,
  parseHandshakeResponse,
  parseMessage,
  type ButtonStates,
  type ClickVariant,
} from './protocol';

export interface ZwiftClickOptions {
  encrypted: boolean;
  handshakeTimeoutMs?: number;
}

export class ZwiftClick implements ConnectableController {
  batteryPct: number | null = null;
  variantKind: ClickVariant = 'unknown';
  locked = false;
  private readonly buttons = new Emitter<ButtonEvent>();
  private readonly conn = new Emitter<boolean>();
  private readonly logs = new Emitter<string>();
  private unsubs: (() => void)[] = [];
  private keys: SessionKeys | null = null;
  private kp: EcdhKeyPair | null = null;
  private states: ButtonStates = {};
  private isConnected = false;
  private manual = false;
  private handshakeDone: (() => void) | null = null;

  constructor(
    private readonly device: BleDevice,
    private readonly opts: ZwiftClickOptions,
  ) {
    device.addEventListener('gattserverdisconnected', this.onDisconnected);
  }

  get name(): string {
    return this.device.name ?? 'Zwift Click';
  }
  get deviceId(): string {
    return this.device.id;
  }
  get connected(): boolean {
    return this.isConnected;
  }
  get wasManualDisconnect(): boolean {
    return this.manual;
  }
  get variant(): string {
    const base =
      this.variantKind === 'click-v1'
        ? 'Click v1'
        : this.variantKind === 'click-v2'
          ? 'Click V2'
          : this.variantKind === 'play'
            ? 'Play'
            : 'Zwift';
    return `${base} (${this.locked ? 'gesperrt' : this.keys ? 'verschlüsselt' : 'unverschlüsselt'})`;
  }

  onButton(cb: (e: ButtonEvent) => void): () => void {
    return this.buttons.on(cb);
  }
  onConnectionChange(cb: (c: boolean) => void): () => void {
    return this.conn.on(cb);
  }
  onLog(cb: (l: string) => void): () => void {
    return this.logs.on(cb);
  }

  private log(msg: string): void {
    this.logs.emit(`${new Date().toISOString().slice(11, 23)} ${msg}`);
  }

  async connect(): Promise<void> {
    this.manual = false;
    const gatt = this.device.gatt;
    if (!gatt) throw new Error('Device has no GATT server');
    const server = gatt.connected ? gatt : await gatt.connect();
    this.cleanup();
    this.keys = null;
    this.states = {};
    const svc = (await tryGetService(server, UUID.ZWIFT)) ?? (await tryGetService(server, UUID.ZWIFT_RIDE));
    if (!svc) {
      this.locked = true;
      throw new Error(
        'Zwift-Service nicht gefunden – Click V2/Ride V2 sind evtl. an Zwift gesperrt oder die Firmware ist veraltet.',
      );
    }
    const [asyncCh, rx, tx] = await this.chars(svc);
    this.unsubs.push(await subscribe(asyncCh, (dv) => this.onData(asBytes(dv), 'async')));
    this.unsubs.push(await subscribe(tx, (dv) => this.onData(asBytes(dv), 'sync')));
    await this.handshake(rx);
    this.isConnected = true;
    this.conn.emit(true);
    void readBattery(server as GattServer).then((b) => {
      if (b !== null && this.batteryPct === null) this.batteryPct = b;
    });
  }

  private async chars(
    svc: GattService,
  ): Promise<[GattCharacteristic, GattCharacteristic, GattCharacteristic]> {
    const a = await svc.getCharacteristic(UUID.ZWIFT_ASYNC);
    const rx = await svc.getCharacteristic(UUID.ZWIFT_SYNC_RX);
    const tx = await svc.getCharacteristic(UUID.ZWIFT_SYNC_TX);
    return [a, rx, tx];
  }

  private async handshake(rx: GattCharacteristic): Promise<void> {
    let payload: Uint8Array;
    if (this.opts.encrypted) {
      this.kp = await generateKeyPair();
      payload = buildEncryptedHandshake(this.kp.publicRaw);
    } else {
      this.kp = null;
      payload = buildPlainHandshake();
    }
    const done = new Promise<void>((resolve) => (this.handshakeDone = resolve));
    this.log(`tx ${toHex(payload)} (${this.opts.encrypted ? 'RideOn + Schlüssel' : 'RideOn'})`);
    await writeChar(rx, payload, false);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<'timeout'>((resolve) => {
      timer = setTimeout(() => resolve('timeout'), this.opts.handshakeTimeoutMs ?? 4000);
    });
    const r = await Promise.race([done.then(() => 'ok' as const), timeout]);
    clearTimeout(timer);
    if (r === 'timeout') {
      // Some firmwares just start streaming. Stay connected; the debug log shows what arrived.
      this.handshakeDone = null;
      this.log('Keine RideOn-Antwort innerhalb des Timeouts – bleibe verbunden und werte Nachrichten aus');
    }
  }

  private onData(bytes: Uint8Array, source: 'async' | 'sync'): void {
    this.log(`rx ${source} ${toHex(bytes)}`);
    if (isRideOn(bytes)) {
      void this.onHandshakeResponse(bytes);
      return;
    }
    if (isLockedResponse(bytes)) {
      this.locked = true;
      this.log('Controller meldet "gesperrt" (Click V2 muss mit Zwift entsperrt werden)');
      return;
    }
    let plain = bytes;
    if (this.keys) {
      try {
        plain = decryptPacket(this.keys, bytes).plain;
      } catch (err) {
        this.log(`decrypt failed: ${String(err)}`);
        return;
      }
    }
    let msg: ReturnType<typeof parseMessage>;
    try {
      msg = parseMessage(plain);
    } catch (err) {
      this.log(`parse failed: ${String(err)}`);
      return;
    }
    if (msg.type === 'buttons') {
      for (const [button, pressed] of Object.entries(msg.states)) {
        if ((this.states[button] ?? false) !== pressed) this.buttons.emit({ button, pressed });
      }
      this.states = msg.states;
    } else if (msg.type === 'battery') {
      this.batteryPct = msg.percent;
    }
  }

  private async onHandshakeResponse(bytes: Uint8Array): Promise<void> {
    const r = parseHandshakeResponse(bytes);
    if (!r) return;
    this.variantKind = r.variant;
    if (this.kp && r.devicePublicKey) {
      try {
        const secret = await sharedSecret(this.kp.privateKey, r.devicePublicKey);
        this.keys = await deriveSessionKeys(secret, r.devicePublicKey, this.kp.publicRaw);
        this.log('Schlüssel abgeleitet (ECDH + HKDF), Nachrichten werden entschlüsselt');
      } catch (err) {
        this.log(`key derivation failed: ${String(err)}`);
      }
    }
    this.handshakeDone?.();
    this.handshakeDone = null;
  }

  async disconnect(): Promise<void> {
    this.manual = true;
    this.cleanup();
    try {
      this.device.gatt?.disconnect();
    } catch {
      /* ignore */
    }
    if (this.isConnected) {
      this.isConnected = false;
      this.conn.emit(false);
    }
  }

  dispose(): void {
    this.device.removeEventListener('gattserverdisconnected', this.onDisconnected);
  }

  private cleanup(): void {
    for (const u of this.unsubs) u();
    this.unsubs = [];
  }

  private readonly onDisconnected = () => {
    this.unsubs = [];
    // release everything that was held
    for (const [button, pressed] of Object.entries(this.states))
      if (pressed) this.buttons.emit({ button, pressed: false });
    this.states = {};
    if (this.isConnected) {
      this.isConnected = false;
      this.conn.emit(false);
    }
  };
}
